import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, RotateCcw, UserPlus, Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { StatusPill, STATUS_LABELS } from "@/components/Status";
import IntegrationsBadge from "@/components/IntegrationsBadge";
import InboundTriagePanel from "@/components/InboundTriagePanel";
import VendorInboxPanel from "@/components/VendorInboxPanel";
import RotatePasscode from "@/components/RotatePasscode";
import ConfirmDialog from "@/components/ConfirmDialog";
import CreateMemberRequestModal from "@/components/staff/CreateMemberRequestModal";
import CreateOrderModal from "@/components/staff/CreateOrderModal";
import { ADMIN, ORDER, TRASH } from "@/constants/testIds";

const STATUS_FILTERS = ["all", "draft", "needs_research", "awaiting_member_approval", "requested", "researching", "placed_with_vendor", "receiving", "ready", "closed", "cancelled"];
const ORDER_TYPE_TABS = [
  { key: "all", label: "All Orders" },
  { key: "Member Profile Order", label: "Member Profile" },
  { key: "Guest Member Order", label: "No Profile" },
  { key: "Golf Shop Order", label: "Golf Shop" },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("active");
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreateRequest, setShowCreateRequest] = useState(false);
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [typeTab, setTypeTab] = useState("all");
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { params: { deleted: tab === "deleted" } };
      if (tab === "active" && filter !== "all") params.params.status = filter;
      const { data } = await api.get("/orders", params);
      setOrders(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [tab, filter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = orders;
    if (typeTab !== "all") list = list.filter((o) => (o.order_type || "Member Profile Order") === typeTab);
    if (!search) return list;
    const q = search.toLowerCase().trim();
    return list.filter((o) =>
      (o.item_name || "").toLowerCase().includes(q) ||
      (o.member_name || "").toLowerCase().includes(q) ||
      (o.member_email || "").toLowerCase().includes(q) ||
      (o.brand || "").toLowerCase().includes(q) ||
      (o.subcategory || "").toLowerCase().includes(q) ||
      (o.category || "").toLowerCase().includes(q) ||
      (o.confirmed_vendor || "").toLowerCase().includes(q) ||
      (o.vendor_po || "").toLowerCase().includes(q) ||
      (o.po_number || "").toLowerCase().includes(q) ||
      (o.tracking_number || "").toLowerCase().includes(q) ||
      (o.club_name || "").toLowerCase().includes(q) ||
      (o.display_order_id || "").toLowerCase().includes(q) ||
      (o.order_id || "").toLowerCase().includes(q),
    );
  }, [orders, search, typeTab]);

  const doConfirm = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      if (pending.action === "delete") {
        await api.delete(`/orders/${pending.order.order_id}`);
      } else {
        await api.post(`/orders/${pending.order.order_id}/restore`);
      }
      setPending(null);
      await load();
      loadStats();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPending(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="overline">Pro Shop</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-2 leading-none">
              Special Orders Ledger
            </h1>
            <p className="text-[#4A4A4A] mt-3 max-w-2xl">
              Every submission from the members&rsquo; lounge, awaiting your attention.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowCreateOrder(true)}
              data-testid="admin-create-order"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1A422D] text-[#F9F8F4] text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#0F2A1C] transition-colors"
            >
              <Plus size={14} /> Create Order
            </button>
            <a
              href="/admin/vendor-contacts"
              data-testid="admin-vendor-directory-link"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#B59A5A] text-[#8a6f33] text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#FBF6E8] transition-colors"
            >
              Vendor Directory
            </a>
            <button
              type="button"
              onClick={() => setShowCreateRequest(true)}
              data-testid="admin-create-member-request"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#1A422D] text-[#1A422D] text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#F3F1E9] transition-colors"
            >
              <UserPlus size={14} /> Member Request
            </button>
            <RotatePasscode />
            <IntegrationsBadge />
          </div>
        </div>

        <CreateMemberRequestModal
          open={showCreateRequest}
          onClose={() => setShowCreateRequest(false)}
          onCreated={() => { setShowCreateRequest(false); load(); }}
        />
        <CreateOrderModal
          open={showCreateOrder}
          onClose={() => setShowCreateOrder(false)}
          onCreated={() => { setShowCreateOrder(false); load(); loadStats(); }}
        />

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          <Stat label="Total" value={stats?.total_orders ?? "—"} testId={ADMIN.statsTotal} highlight />
          <Stat label="Members" value={stats?.members ?? "—"} testId={ADMIN.statsMembers} />
          {["requested", "researching", "placed_with_vendor", "receiving", "ready"].map((s) => (
            <Stat key={s} label={STATUS_LABELS[s]} value={stats?.by_status?.[s] ?? 0} />
          ))}
        </div>

        {/* Active / Deleted tab */}
        <div className="mt-10 inline-flex border border-[#E5E3D8] bg-white">
          <button
            type="button"
            onClick={() => setTab("active")}
            data-testid={TRASH.tabActive}
            className={`px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold transition-colors ${
              tab === "active"
                ? "bg-[#1A422D] text-[#F9F8F4]"
                : "text-[#1A422D] hover:bg-[#F3F1E9]"
            }`}
          >
            Active Orders
          </button>
          <button
            type="button"
            onClick={() => setTab("deleted")}
            data-testid={TRASH.tabDeleted}
            className={`px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold transition-colors border-l border-[#E5E3D8] ${
              tab === "deleted"
                ? "bg-[#8a1a1a] text-[#F9F8F4]"
                : "text-[#8a1a1a] hover:bg-[#fbe9e9]"
            }`}
          >
            Deleted
          </button>
        </div>

        {tab === "deleted" && (
          <p className="text-xs text-[#7A7A7A] mt-3 max-w-xl">
            Soft-deleted orders are kept for 5 days. Restore brings them back into the active queue.
          </p>
        )}

        {/* Filters */}
        {tab === "active" && (
          <>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-order-type-tabs">
              {ORDER_TYPE_TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTypeTab(t.key)}
                  data-testid={`admin-order-type-${t.key.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`px-4 py-2 text-[11px] uppercase tracking-[0.22em] font-semibold border transition-colors ${
                    typeTab === t.key
                      ? "bg-[#B59A5A] text-white border-[#B59A5A]"
                      : "bg-white text-[#B59A5A] border-[#E5E3D8] hover:border-[#B59A5A]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
            <div data-testid={ADMIN.filterStatus} className="flex flex-wrap gap-2">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => setFilter(s)}
                  className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${
                    filter === s
                      ? "bg-[#1A422D] text-white border-[#1A422D]"
                      : "bg-white text-[#1A422D] border-[#E5E3D8] hover:border-[#1A422D]"
                  }`}
                >
                  {s === "all" ? "All" : STATUS_LABELS[s]}
                </button>
              ))}
            </div>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search PO #, member, brand, item…"
              data-testid="admin-search-input"
              className="input-field max-w-xs ml-auto"
            />
          </div>
          </>
        )}

        {error && (
          <div className="mt-6 px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="mt-6 surface-card overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-[#F3F1E9]">
              <tr className="text-xs uppercase tracking-wider text-[#4A4A4A]">
                <th className="px-6 py-4">Member</th>
                <th className="px-6 py-4">Item</th>
                <th className="px-6 py-4">Specs</th>
                <th className="px-6 py-4">{tab === "deleted" ? "Deleted" : "Submitted"}</th>
                <th className="px-6 py-4">{tab === "deleted" ? "Permanent in" : "Status"}</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-6 py-8 text-center text-[#7A7A7A]">Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-12 text-center">
                  <div className="overline">{tab === "deleted" ? "Trash is Empty" : "Quiet day at the shop"}</div>
                  <div className="font-serif text-2xl text-[#1A1A1A] mt-2">
                    {tab === "deleted" ? "Nothing here." : "No orders to show."}
                  </div>
                </td></tr>
              )}
              {filtered.map((o) => (
                <tr
                  key={o.order_id}
                  data-testid={`${ORDER.rowPrefix}${o.order_id}`}
                  className="border-t border-[#E5E3D8] hover:bg-[#FAF9F2]"
                >
                  <td className="px-6 py-4">
                    <div className="font-serif text-lg text-[#1A1A1A]">{o.member_name}</div>
                    <div className="text-xs text-[#7A7A7A]">{o.member_email}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-[#1A1A1A]">{o.item_name}</div>
                    {o.brand && (
                      <div className="text-xs text-[#B59A5A] uppercase tracking-wider" data-testid={`row-brand-${o.order_id}`}>{o.brand}</div>
                    )}
                    <div className="text-xs text-[#7A7A7A] uppercase tracking-wider">
                      {[o.category, o.subcategory].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-[#4A4A4A]">
                    {[o.color, o.size, `Qty ${o.quantity}`].filter(Boolean).join(" · ")}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#4A4A4A]">
                    {new Date(tab === "deleted" ? o.deleted_at : o.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    {tab === "deleted" ? (
                      <span
                        data-testid={`${TRASH.countdownPrefix}${o.order_id}`}
                        className="inline-block px-3 py-1 text-xs uppercase tracking-wider border border-[#8a1a1a] text-[#8a1a1a] bg-[#fbe9e9]"
                      >
                        {o.deletes_in_days > 0
                          ? `${o.deletes_in_days} day${o.deletes_in_days === 1 ? "" : "s"}`
                          : "Today"}
                      </span>
                    ) : (
                      <StatusPill status={o.status} />
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {tab === "deleted" ? (
                        <button
                          type="button"
                          onClick={() => setPending({ action: "restore", order: o })}
                          data-testid={`${TRASH.restorePrefix}${o.order_id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider text-[#1A422D] border border-[#1A422D] hover:bg-[#E9F0EB] transition-colors"
                          aria-label="Restore order"
                        >
                          <RotateCcw size={14} /> Restore
                        </button>
                      ) : (
                        <>
                          <Link to={`/orders/${o.order_id}`} className="text-sm text-[#1A422D] underline underline-offset-4">
                            Open
                          </Link>
                          <button
                            type="button"
                            onClick={() => setPending({ action: "delete", order: o })}
                            data-testid={`${TRASH.deletePrefix}${o.order_id}`}
                            className="inline-flex items-center justify-center p-2 text-[#7A7A7A] hover:text-[#8a1a1a] hover:bg-[#fbe9e9] transition-colors"
                            aria-label="Delete order"
                            title="Delete this order"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <InboundTriagePanel />
        <VendorInboxPanel />
      </div>

      <ConfirmDialog
        open={!!pending}
        tone={pending?.action === "delete" ? "danger" : "default"}
        title={
          pending?.action === "delete"
            ? `Delete "${pending.order.item_name}"?`
            : `Restore "${pending?.order?.item_name}"?`
        }
        message={
          pending?.action === "delete"
            ? "Moves it to the Deleted tab. You have 5 days to restore before it's purged permanently."
            : "Brings the order back into the active ledger."
        }
        confirmLabel={pending?.action === "delete" ? "Delete Order" : "Restore Order"}
        onConfirm={doConfirm}
        onCancel={() => setPending(null)}
        busy={busy}
      />
    </div>
  );
}

function Stat({ label, value, testId, highlight }) {
  return (
    <div data-testid={testId} className={`p-5 border ${highlight ? "border-[#1A422D] bg-white" : "border-[#E5E3D8] bg-white"}`}>
      <div className="label-elegant">{label}</div>
      <div className="font-serif text-3xl mt-1 text-[#1A1A1A]">{value}</div>
    </div>
  );
}
